import { Editor, JSONContent } from '@tiptap/core'
import { Transaction } from '@tiptap/pm/state'
import { TRACK_CHANGES_SKIP_META, trackChangesPluginKey } from './track-changes-plugin'

/**
 * Marks a transaction as content the server handed down rather than an edit the reader made.
 *
 * The structure guards in this directory reject edits that would break the shape the export reads. A remote
 * edit is already exempt — it carries another client's change, which that client's own guards have passed. A
 * reset is not: it replaces the whole document with the server's copy through a local transaction, and trips
 * every guard on the way. It is authoritative, so it says so.
 */

const AUTHORITATIVE_CONTENT_META = 'authoritativeContent'

function markAuthoritativeContent(transaction: Transaction): Transaction {
  // Track changes would otherwise record the whole replaced document as this reader's insertion, leaving them
  // a suggestion to review over content they did not write.
  return transaction.setMeta(AUTHORITATIVE_CONTENT_META, true).setMeta(trackChangesPluginKey, TRACK_CHANGES_SKIP_META)
}

export function isAuthoritativeContent(transaction: Transaction): boolean {
  return transaction.getMeta(AUTHORITATIVE_CONTENT_META) === true
}

/**
 * Replaces the document with content the server owns, in one transaction the structure guards will let past.
 *
 * Returns false, or throws through `errorOnInvalidContent`, rather than letting content the schema cannot parse
 * fall back to an empty document — this replaces the whole of what the reader has, so it is the one path where
 * a malformed response would cost them their minutes.
 */
export function replaceWithAuthoritativeContent(editor: Editor, content: JSONContent | null | undefined): boolean {
  if (content === null || content === undefined) {
    return false
  }

  return editor
    .chain()
    .command(({ tr }) => {
      markAuthoritativeContent(tr)
      return true
    })
    .setContent(content, { errorOnInvalidContent: true })
    .run()
}
